// @flow

import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {of} from 'rxjs';
import {ofType, StateObservable} from 'redux-observable';
import {
  map,
  switchMap,
  debounceTime,
  concatMap,
  catchError,
} from 'rxjs/operators';
import {
  createPromotionApi,
  getPromotionApi,
  getPromotionsApi,
  endpoints,
  editPromotionApi,
  sortPromotionApi,
} from './api';
import {buildRoute} from '../../../http';
import {createMultiFieldFormData} from '../../../utils/form';
import {uploadImageStoreApi} from '../../file-storage/api';
import {getFeatureSpec} from '../../../utils/component';
import type {RootState} from '../..';
import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../../store/utils';
import type {ApiError} from '../../../http/error';
import type {SinglePromotion, StoredFile} from '../../../entities';
import type {PromotionRequest} from './api';

export type PromotionsAction = Action & {
  promotions?: SinglePromotion[],
  promotion?: SinglePromotion,
  promotionRequest?: PromotionRequest,
  error?: string | ApiError,
  promotionId?: string,
};

export type PromotionsState = {
  isFetchingPromotions?: boolean,
  isFetchingPromotion?: boolean,
  isSaving?: boolean,
  selectedState?: string,
  promotion?: SinglePromotion,
  promotions?: SinglePromotion[],
  error?: string,
  createPromotionSuccess?: boolean,
  updatePromotionSuccess?: boolean,
  deletePromotionSuccess?: boolean,
  sortPromotionSuccess?: boolean,
  sortPromotionError?: string,
};

export class ResetPromotionState extends ActionCreator<PromotionsAction> {}
export class ResetPromotion extends ActionCreator<PromotionsAction> {}

export class FetchPromotions extends ActionCreator<PromotionsAction> {}
export class FetchPromotionsSuccess extends ActionCreator<PromotionsAction> {}
export class FetchPromotionsErrorFn extends ActionCreator<PromotionsAction> {}

export class FetchPromotion extends ActionCreator<PromotionsAction> {}
export class FetchPromotionSuccess extends ActionCreator<PromotionsAction> {}
export class FetchPromotionErrorFn extends ActionCreator<PromotionsAction> {}

export class CreatePromotion extends ActionCreator<PromotionsAction> {}
export class CreatePromotionSuccess extends ActionCreator<PromotionsAction> {}
export class CreatePromotionErrorFn extends ActionCreator<PromotionsAction> {}

export class UpdatePromotion extends ActionCreator<PromotionsAction> {}
export class UpdatePromotionSuccess extends ActionCreator<PromotionsAction> {}
export class UpdatePromotionErrorFn extends ActionCreator<PromotionsAction> {}

export class SortPromotion extends ActionCreator<PromotionsAction> {}
export class SortPromotionSuccess extends ActionCreator<PromotionsAction> {}
export class SortPromotionErrorFn extends ActionCreator<PromotionsAction> {}

export class CleanPromotion extends ActionCreator<PromotionsAction> {}
export class ToggleFilter extends ActionCreator<PromotionsAction> {}

export const actions = {
  resetPromotionState: () => new ResetPromotionState(),
  fetchPromotions: (payload: PromotionsAction) => new FetchPromotions(payload),
  fetchPromotion: (payload: PromotionsAction) => new FetchPromotion(payload),
  createPromotion: (payload: PromotionsAction) => new CreatePromotion(payload),
  updatePromotion: (payload: PromotionsAction) => new UpdatePromotion(payload),
  sortPromotion: (payload: PromotionsAction) => new SortPromotion(payload),
  cleanPromotion: () => new CleanPromotion(),
  resetPromotion: () => new ResetPromotion(),
  toggleFilter: (promotionState?: PromotionsState) =>
    new ToggleFilter({
      payload: promotionState,
    }),
};
export const initialState: PromotionsState = {
  selectedState: '0',
};

export default createReducer<PromotionsState>(initialState, {
  [FetchPromotions.type]: (state: PromotionsState) => ({
    ...state,
    isFetchingPromotions: true,
    promotions: undefined,
  }),
  [FetchPromotionsSuccess.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isFetchingPromotions: false,
    error: undefined,
    promotions: action.data,
  }),
  [FetchPromotionsErrorFn.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isFetchingPromotions: false,
    error: action.error,
  }),
  [FetchPromotion.type]: (state: PromotionsState) => ({
    ...state,
    promotion: undefined,
    isFetchingPromotion: true,
  }),
  [FetchPromotionSuccess.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isFetchingPromotion: false,
    error: undefined,
    promotion: action.data,
  }),
  [FetchPromotionErrorFn.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isFetchingPromotion: false,
    error: action.error,
  }),
  [CreatePromotion.type]: (state: PromotionsState) => ({
    ...state,
    createPromotionSuccess: undefined,
    isSaving: true,
    error: undefined,
  }),
  [CreatePromotionSuccess.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    promotions: [...(state.promotions || []), action.data],
    isSaving: undefined,
    error: undefined,
    createPromotionSuccess: true,
    promotion: action.data,
  }),
  [CreatePromotionErrorFn.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isSaving: false,
    createPromotionSuccess: undefined,
    error: action.error,
  }),
  [UpdatePromotion.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isSaving: true,
    updatePromotionSuccess: undefined,
    error: undefined,
  }),
  [UpdatePromotionSuccess.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => {
    return {
      ...state,
      isSaving: false,
      updatePromotionSuccess: true,
      error: undefined,
    };
  },
  [UpdatePromotionErrorFn.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isSaving: false,
    updatePromotionSuccess: undefined,
    error: action.error,
  }),
  [SortPromotion.type]: (state: PromotionsState) => ({
    ...state,
    isSaving: true,
    sortPromotionSuccess: undefined,
    sortPromotionError: undefined,
  }),
  [SortPromotionSuccess.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isSaving: false,
    sortPromotionSuccess: true,
    sortPromotionError: undefined,
    // promotions: action.promotions,
  }),
  [SortPromotionErrorFn.type]: (
    state: PromotionsState,
    action: PromotionsAction
  ) => ({
    ...state,
    isSaving: false,
    sortPromotionError: action.error,
  }),
  [ResetPromotionState.type]: (state: PromotionsState) => ({
    ...state,
    promotion: undefined,
    createPromotionSuccess: undefined,
    error: undefined,
  }),
  [ResetPromotion.type]: (state: PromotionsState) => ({
    ...state,
    //promotion: undefined,
    //deletePromotionSuccess : undefined,
    // updatePromotionSuccess: undefined,
    error: undefined,
  }),
  [CleanPromotion.type]: (state: PromotionsState) => ({
    ...state,
    promotion: undefined,
    updatePromotionSuccess: undefined,
  }),
  [ToggleFilter.type]: (
    state: PromotionsState,
    {payload}: PromotionsAction
  ) => ({
    ...state,
    selectedState: payload || '',
  }),
});

export const fetchPromotionsEpic = (
  action$: ActionsObservable,
  state$: StateObservable
) =>
  action$.pipe(
    ofType(
      FetchPromotions.type,
      SortPromotionSuccess.type,
      UpdatePromotionSuccess.type,
      CreatePromotionSuccess.type
    ),
    debounceTime(250),
    switchMap(() => {
      const selectedState = state$.value.promotions.selectedState;
      const queryFilter = selectedState === '1' ? {mode: 'ALL'} : {};
      return getPromotionsApi(queryFilter).pipe(
        map(data => new FetchPromotionsSuccess({data: data})),
        buildCatchError(
          FetchPromotionsErrorFn,
          'Error al obtener las promociones'
        )
      );
    })
  );

export const fetchPromotionEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchPromotion.type),
    debounceTime(250),
    switchMap(({promotionId}: PromotionsAction) =>
      getPromotionApi(undefined, {
        route: buildRoute(endpoints.GET_PROMOTION, [promotionId]),
      }).pipe(
        map(data => new FetchPromotionSuccess({data: data})),
        buildCatchError(FetchPromotionErrorFn)
      )
    )
  );

export function createPromotionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreatePromotion.type),
    debounceTime(500),
    switchMap(({promotionRequest}: {promotionRequest: PromotionRequest}) => {
      const files = [];
      if (promotionRequest.promotionImage)
        files.push(promotionRequest.promotionImage);
      if (promotionRequest.promotionSmallImage)
        files.push(promotionRequest.promotionSmallImage);

      if (files.length > 0)
        return uploadImageStoreApi(
          createMultiFieldFormData(files, undefined, {
            filePrefix: 'im/promotion',
          })
        ).pipe(
          map((fileImages: StoredFile[]) => [
            fileImages,
            promotionRequest,
            undefined,
          ]),
          catchError((error: ApiError) => [undefined, undefined, error])
        );
      else return of([[], promotionRequest, undefined]);
    }),
    concatMap(
      ([
        fileImages: StoredFile[],
        promotionRequest: PromotionRequest,
        pipeError: ApiError,
      ]) => {
        if (pipeError)
          return of(new CreatePromotionErrorFn({error: pipeError}));
        if (fileImages && fileImages.length === 1) {
          if (promotionRequest.promotionImage) {
            promotionRequest.promotionImageId = fileImages[0].id;
          } else if (promotionRequest.promotionSmallImage) {
            promotionRequest.promotionSmallImageId = fileImages[0].id;
          }
        } else if (fileImages && fileImages.length === 2) {
          if (fileImages && fileImages[0]) {
            promotionRequest.promotionImageId = fileImages[0].id;
          }
          if (fileImages && fileImages[1]) {
            promotionRequest.promotionSmallImageId = fileImages[1].id;
          }
        }

        delete promotionRequest.promotionImage;
        delete promotionRequest.promotionSmallImage;

        return createPromotionApi(promotionRequest).pipe(
          map(data => new CreatePromotionSuccess({data: data})),
          buildCatchError(CreatePromotionErrorFn, 'Error al crear la promoción')
        );
      }
    )
  );
}

export const updatePromotionEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(UpdatePromotion.type),
    debounceTime(500),
    switchMap(
      ({
        promotionRequest,
        promotionId,
      }: {
        promotionRequest: PromotionRequest,
        promotionId: string,
      }) => {
        const files = [];
        if (promotionRequest.promotionImage)
          files.push(promotionRequest.promotionImage);
        if (promotionRequest.promotionSmallImage)
          files.push(promotionRequest.promotionSmallImage);

        if (files.length > 0)
          return uploadImageStoreApi(
            createMultiFieldFormData(files, undefined, {
              filePrefix: 'im/promotion',
            })
          ).pipe(
            map((fileImages: StoredFile[]) => [
              fileImages,
              promotionRequest,
              undefined,
              promotionId,
            ]),
            catchError((error: ApiError) => [
              undefined,
              undefined,
              error,
              promotionId,
            ])
          );
        else return of([[], promotionRequest, undefined, promotionId]);
      }
    ),
    concatMap(
      ([
        fileImages: StoredFile[],
        promotionRequest: PromotionRequest,
        error: ApiError,
        promotionId: string,
      ]) => {
        if (error) return of(new UpdatePromotionErrorFn({error}));
        if (promotionRequest.files) {
          if (promotionRequest && !promotionRequest.landscapeImage)
            promotionRequest.promotionImageId = '';
          if (promotionRequest && !promotionRequest.portraitImage)
            promotionRequest.promotionSmallImageId = '';

          if (fileImages && fileImages.length === 1) {
            if (promotionRequest.promotionImage) {
              promotionRequest.promotionImageId = fileImages[0].id;
            } else if (promotionRequest.promotionSmallImage) {
              promotionRequest.promotionSmallImageId = fileImages[0].id;
            }
          } else if (fileImages && fileImages.length === 2) {
            if (fileImages && fileImages[0]) {
              promotionRequest.promotionImageId = fileImages[0].id;
            }
            if (fileImages && fileImages[1]) {
              promotionRequest.promotionSmallImageId = fileImages[1].id;
            }
          }
          delete promotionRequest.landscapeImage;
          delete promotionRequest.portraitImage;
          delete promotionRequest.promotionImage;
          delete promotionRequest.promotionSmallImage;
        }
        delete promotionRequest.files;

        return editPromotionApi(promotionRequest, {
          route: buildRoute(endpoints.EDIT_PROMOTION, [promotionId]),
        }).pipe(
          map(data => new UpdatePromotionSuccess({data: data})),
          catchError(err => {
            if (err.error && err.error.message) {
              const {message} = err.error;
              const store =
                state$.value && state$.value.store && state$.value.store.store;
              const promotionSpec = getFeatureSpec('STORE_PROMOTION', store);
              let errorMessage = 'Error al actualizar la promoción';
              switch (message) {
                case 'Max enabled promotions limit':
                  if (promotionSpec && promotionSpec.limit) {
                    const moreThanOne = promotionSpec.limit > 1;
                    errorMessage = `No puedes tener más de ${
                      promotionSpec.limit
                    } ${moreThanOne ? 'promociones' : ' promoción'} activa${
                      moreThanOne ? 's' : ''
                    }.`;
                  }
                  break;
                default:
                  break;
              }
              return of(new UpdatePromotionErrorFn({error: errorMessage}));
            }
            return of(
              new UpdatePromotionErrorFn({
                error: 'Error al actualizar la promoción',
              })
            );
          })
        );
      }
    )
  );

export function sortPromotionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SortPromotion.type),
    switchMap(({sortPromotionRequest}: PromotionsAction) => {
      const {init, end} = sortPromotionRequest;
      return sortPromotionApi({
        targetPromotionId: end.id,
        originPromotionId: init.id,
      }).pipe(
        map(
          (promotions: SinglePromotion[]) =>
            new SortPromotionSuccess({promotions})
        ),
        buildCatchError(
          SortPromotionErrorFn,
          'Error al ordenar las categorias.'
        )
      );
    })
  );
}
